import { useSyncExternalStore } from "react";

/** Fixed owner-set rate: 2.70 XCD = 1 USD. */
export const XCD_PER_USD = 2.7;
export type DisplayCurrency = "XCD" | "USD";

const KEY = "shop:currency:v1";
// Eastern Caribbean Currency Union members.
const ECCU_TZ = [
  "America/St_Kitts", "America/Antigua", "America/Dominica", "America/Grenada",
  "America/St_Lucia", "America/St_Vincent", "America/Montserrat", "America/Anguilla",
];
const ECCU_REGIONS = ["KN", "AG", "DM", "GD", "LC", "VC", "MS", "AI"];

function detect(): DisplayCurrency {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (ECCU_TZ.includes(tz)) return "XCD";
    const langs = navigator.languages ?? [navigator.language];
    if (langs.some((l) => ECCU_REGIONS.includes(l.split("-")[1]?.toUpperCase()))) return "XCD";
  } catch { /* ignore */ }
  return "USD";
}

let current: DisplayCurrency =
  typeof window === "undefined"
    ? "USD"
    : ((localStorage.getItem(KEY) as DisplayCurrency | null) ?? detect());
const listeners = new Set<() => void>();

export function setDisplayCurrency(c: DisplayCurrency) {
  current = c;
  localStorage.setItem(KEY, c);
  listeners.forEach((l) => l());
}

export function getDisplayCurrency(): DisplayCurrency {
  return current;
}

export function useDisplayCurrency(): DisplayCurrency {
  return useSyncExternalStore(
    (cb) => (listeners.add(cb), () => listeners.delete(cb)),
    () => current,
    () => "USD",
  );
}

/** Convert a store price (XCD unless stated) into the display currency. */
export function convert(amount: number, from: string, to: DisplayCurrency): number {
  const xcd = from === "USD" ? amount * XCD_PER_USD : amount;
  return to === "USD" ? Math.round((xcd / XCD_PER_USD) * 100) / 100 : Math.round(xcd * 100) / 100;
}

export function formatMoney(amount: number | string, from: string, to: DisplayCurrency): string {
  const v = convert(typeof amount === "string" ? parseFloat(amount) : amount, from, to);
  return `${to === "XCD" ? "EC$" : "US$"}${v.toFixed(2)}`;
}

/** Hook-friendly formatter. */
export function usePriceFormatter() {
  const to = useDisplayCurrency();
  return (price: { amount: string | number; currencyCode: string }) =>
    formatMoney(price.amount, price.currencyCode, to);
}
